import React from 'react';
import { Settings } from '../types';
import { X, Volume2, VolumeX, Zap, Monitor, Trash2, ListTodo } from 'lucide-react';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  settings: Settings;
  onSettingsChange: (settings: Settings) => void;
  onClearData: () => void;
  onOpenRoadmap?: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
  onClearData,
  onOpenRoadmap,
}) => {
  if (!isOpen) return null;

  const handleAudioToggle = () => {
    onSettingsChange({
      ...settings,
      audio: { ...settings.audio, enabled: !settings.audio.enabled },
    });
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSettingsChange({
      ...settings,
      audio: { ...settings.audio, volume: parseInt(e.target.value, 10) },
    });
  };

  const handleGlowToggle = () => {
    onSettingsChange({
      ...settings,
      graphics: { ...settings.graphics, glowEffects: !settings.graphics.glowEffects },
    });
  };

  const handleParticleLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSettingsChange({
      ...settings,
      graphics: { ...settings.graphics, particleLimit: parseInt(e.target.value, 10) },
    });
  };

  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSettingsChange({
      ...settings,
      simulation: { ...settings.simulation, speed: parseFloat(e.target.value) },
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Monitor className="w-5 h-5 text-blue-400" />
            Settings
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Audio Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <Volume2 className="w-4 h-4" />
              Audio
            </h3>
            
            <div className="flex items-center justify-between">
              <span className="text-white">Enable Sound</span>
              <button
                onClick={handleAudioToggle}
                className={`p-2 rounded-lg transition-colors ${
                  settings.audio.enabled
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-gray-700 text-gray-400'
                }`}
              >
                {settings.audio.enabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Volume</span>
                <span className="text-gray-400">{settings.audio.volume}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={settings.audio.volume}
                onChange={handleVolumeChange}
                disabled={!settings.audio.enabled}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Graphics Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Graphics
            </h3>
            
            <div className="flex items-center justify-between">
              <span className="text-white">Glow Effects</span>
              <button
                onClick={handleGlowToggle}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  settings.graphics.glowEffects
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'bg-gray-700 text-gray-400'
                }`}
              >
                {settings.graphics.glowEffects ? 'On' : 'Off'}
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Particle Limit</span>
                <span className="text-gray-400">{settings.graphics.particleLimit}</span>
              </div>
              <input
                type="range"
                min="500"
                max="5000"
                step="100"
                value={settings.graphics.particleLimit}
                onChange={handleParticleLimitChange}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
            </div>
          </div>

          {/* Simulation Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Simulation
            </h3>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Simulation Speed</span>
                <span className="text-gray-400">{settings.simulation.speed}x</span>
              </div>
              <input
                type="range"
                min="0.25"
                max="4"
                step="0.25"
                value={settings.simulation.speed}
                onChange={handleSpeedChange}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0.25x</span>
                <span>1x</span>
                <span>4x</span>
              </div>
            </div>
          </div>

          {/* Data Section */}
          <div className="space-y-3 pt-4 border-t border-gray-700">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Data
            </h3>
            
            <button
              onClick={onClearData}
              className="w-full py-2 px-4 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Clear All Data
            </button>
          </div>

          {/* Roadmap (moved from toolbar) */}
          {onOpenRoadmap && (
            <div className="space-y-3 pt-4 border-t border-gray-700">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <ListTodo className="w-4 h-4" />
                Development
              </h3>
              <button
                onClick={() => {
                  onClose();
                  onOpenRoadmap();
                }}
                className="w-full py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <ListTodo className="w-4 h-4" />
                Open Roadmap
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
